/**
 * UnderstandingChip · the trust-contract chip · 9 Feb 2026.
 *
 * Founder direction (breach closure): "a member always knows whether
 * the AI truly heard them". Every candidate card on /table, /mira, and
 * any future surface (/club, /travel) renders this chip when the
 * member's query carried an explicit ask (dietary · accessibility ·
 * vibe). The chip is the visible bridge between Mira's understanding
 * and the candidate's reality.
 *
 * Two states (and only two · doctrine: never silent, never invented):
 *   ✓ matched   — "Eggless · per Concierge®" / "Vegan · per Google"
 *                  the chip cites the SOURCE that attested the match.
 *   ⚠ unmet     — "Eggless · Concierge® will confirm"
 *                  the chip is HONEST that the AI couldn't verify it
 *                  from our data, and the desk will hand-check before
 *                  the venue is sent.
 *
 * Lives under /components/concierge/ from day one so /mira can render
 * the SAME chip when the shared engine wires through. No /table-only
 * version that later needs a copy on /mira.
 */
import React from "react";


function _normaliseSource(src) {
    // Map the various source labels to short human prefixes.
    if (!src) return null;
    const s = src.toLowerCase();
    if (s.includes("concierge")) return "per Concierge®";
    if (s.includes("google")) return "per Google";
    if (s.includes("places")) return "per Google";
    if (s.includes("desk")) return "per Concierge®";
    return null;
}


export function UnderstandingMatchedChip({ label, source, testId }) {
    const prefix = _normaliseSource(source) || "verified";
    return (
        <span
            data-testid={testId || `understanding-chip-matched-${label.toLowerCase()}`}
            data-state="matched"
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 11,
                lineHeight: 1.1,
                color: "var(--t-accent, #2f7a3e)",
                background: "rgba(47, 122, 62, 0.08)",
                border: "1px solid rgba(47, 122, 62, 0.28)",
                borderRadius: 999,
                padding: "4px 10px",
                fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                whiteSpace: "nowrap",
            }}
            title={`Member asked for ${label}. Attested ${prefix}.`}
        >
            <span aria-hidden style={{ fontWeight: 700 }}>✓</span>
            <span>{label}</span>
            <span style={{ opacity: 0.7, fontSize: 10 }}>· {prefix}</span>
        </span>
    );
}


export function UnderstandingUnmetChip({ label, hedge, testId }) {
    const hedgeText = hedge || "Concierge® will confirm";
    return (
        <span
            data-testid={testId || `understanding-chip-unmet-${label.toLowerCase()}`}
            data-state="unmet"
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontSize: 11,
                lineHeight: 1.1,
                color: "var(--t-ink2)",
                background: "var(--t-paper2)",
                border: "1px dashed var(--t-line2)",
                borderRadius: 999,
                padding: "4px 10px",
                fontFamily: "'Hanken Grotesk', system-ui, sans-serif",
                whiteSpace: "nowrap",
            }}
            title={(
                `We could not verify "${label}" from our data. ` +
                "Concierge® will confirm before sending."
            )}
        >
            <span aria-hidden style={{ fontWeight: 700, opacity: 0.7 }}>⚠</span>
            <span>{label}</span>
            <span style={{ opacity: 0.65, fontSize: 10 }}>· {hedgeText}</span>
        </span>
    );
}


/**
 * UnderstandingChipRow · renders an entire understanding_match object
 * (the shape the agent plan attaches to each candidate) as a wrapping
 * row of chips. Matched chips render first (greens), unmet chips
 * after (dashed slate). When BOTH lists are empty the row collapses.
 */
export function UnderstandingChipRow({ match, testIdPrefix = "understanding-row" }) {
    if (!match) return null;
    const matched = match.matched || {};
    const unmet = match.unmet || {};
    const matchedDiet = matched.dietary || [];
    const matchedAcc = matched.accessibility || [];
    const unmetDiet = unmet.dietary || [];
    const unmetAcc = unmet.accessibility || [];
    const total = matchedDiet.length + matchedAcc.length + unmetDiet.length + unmetAcc.length;
    if (total === 0) return null;
    return (
        <div
            data-testid={testIdPrefix}
            style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginTop: 8,
            }}
        >
            {matchedDiet.map((m, i) => (
                <UnderstandingMatchedChip
                    key={`md-${i}`}
                    label={m.label}
                    source={m.source}
                    testId={`${testIdPrefix}-matched-${(m.ask || m.label).toLowerCase()}`}
                />
            ))}
            {matchedAcc.map((m, i) => (
                <UnderstandingMatchedChip
                    key={`ma-${i}`}
                    label={m.label}
                    source={m.source}
                    testId={`${testIdPrefix}-matched-accessibility-${i}`}
                />
            ))}
            {unmetDiet.map((u, i) => (
                <UnderstandingUnmetChip
                    key={`ud-${i}`}
                    label={u.label}
                    hedge={match.unmet_hedge}
                    testId={`${testIdPrefix}-unmet-${(u.ask || u.label).toLowerCase()}`}
                />
            ))}
            {unmetAcc.map((u, i) => (
                <UnderstandingUnmetChip
                    key={`ua-${i}`}
                    label={u.label}
                    hedge={match.unmet_hedge}
                    testId={`${testIdPrefix}-unmet-accessibility-${i}`}
                />
            ))}
        </div>
    );
}
